import { currentPrice, money, } from '../catalog/prices.js';
import { normalizeIngredientName } from '../ingredients/normalize-name.js';
import { Dec, dec } from '../quantity/decimal.js';
import { getUnit } from '../units/catalog.js';
function indexPrices(observations, products) {
    const byProduct = new Map();
    const byIngredient = new Map();
    for (const observation of observations) {
        if (observation.productId)
            byProduct.set(observation.productId, [
                ...(byProduct.get(observation.productId) ?? []),
                observation,
            ]);
        else if (observation.ingredientId)
            byIngredient.set(observation.ingredientId, [
                ...(byIngredient.get(observation.ingredientId) ?? []),
                observation,
            ]);
    }
    const productsByIngredient = new Map();
    for (const product of products)
        if (product.ingredientId)
            productsByIngredient.set(product.ingredientId, [
                ...(productsByIngredient.get(product.ingredientId) ?? []),
                product,
            ]);
    return {
        byProduct,
        byIngredient,
        productsByIngredient,
        products: new Map(products.map((p) => [p.id, p])),
    };
}
const learnedProduct = (learned, name) => {
    if (!learned)
        return undefined;
    const key = normalizeIngredientName(name);
    if (learned instanceof Map)
        return learned.get(key);
    return learned[key];
};
/** Units in which things are sold: "2 Dosen" are two sales units. */
const SALES_UNITS = new Set(['package', 'can', 'jar', 'bottle', 'sachet', 'bunch', 'stick']);
/**
 * How often the price counts: pieces and packages multiply it, a wanted weight is rounded up to
 * whole packages, a per-kg price is multiplied by the wanted weight; when the units cannot be
 * related the price counts once.
 */
export function priceMultiplier(item, observation, product) {
    const one = dec(1);
    if (!item.quantity)
        return one;
    const wanted = dec(item.quantity);
    if (!wanted.greaterThan(0))
        return one;
    const wantedUnit = item.unitCode ? getUnit(item.unitCode) : undefined;
    // Loose goods: "2,99 per kg" × the wanted weight.
    if (observation.perQuantity && observation.perUnitCode) {
        const perUnit = getUnit(observation.perUnitCode);
        const per = dec(observation.perQuantity);
        if (!wantedUnit ||
            !perUnit ||
            wantedUnit.dimension !== perUnit.dimension ||
            !per.greaterThan(0))
            return one;
        if (wantedUnit.dimension !== 'MASS' && wantedUnit.dimension !== 'VOLUME')
            return one;
        return wanted.times(wantedUnit.baseFactor).div(per.times(perUnit.baseFactor));
    }
    // "3 Joghurt", "2 Dosen", "1 Packung": so many sales units.
    if (!item.unitCode || wantedUnit?.code === 'piece' || SALES_UNITS.has(wantedUnit?.code ?? ''))
        return wanted.ceil();
    if (!wantedUnit || (wantedUnit.dimension !== 'MASS' && wantedUnit.dimension !== 'VOLUME'))
        return one;
    // "500 g" of a 250 g package counts two packages.
    const size = observation.packageQuantity ?? product?.packageQuantity ?? null;
    const sizeUnit = getUnit(observation.packageUnitCode ?? product?.packageUnitCode ?? null);
    if (!size || !sizeUnit || sizeUnit.dimension !== wantedUnit.dimension)
        return one;
    const packageBase = dec(size).times(sizeUnit.baseFactor);
    if (!packageBase.greaterThan(0))
        return one;
    const packages = wanted.times(wantedUnit.baseFactor).div(packageBase).ceil();
    return packages.lessThan(1) ? one : packages;
}
function resolveItemPrice(item, index, options) {
    const at = (observations) => observations?.length ? currentPrice(observations, options) : null;
    // 1. the item's own product
    if (item.productId) {
        const price = at(index.byProduct.get(item.productId));
        if (price)
            return { price, via: 'ITEM_PRODUCT', product: index.products.get(item.productId) ?? null };
    }
    // 2. the product this household bought for that name – when it has a price at this chain
    const learnedId = learnedProduct(options.learned, item.name);
    if (learnedId && learnedId !== item.productId) {
        const price = at(index.byProduct.get(learnedId));
        if (price)
            return { price, via: 'LEARNED', product: index.products.get(learnedId) ?? null };
    }
    // 3. the cheapest current price among the household's products of the item's ingredient
    const ingredientId = item.ingredientId ??
        (item.productId ? (index.products.get(item.productId)?.ingredientId ?? null) : null);
    if (ingredientId) {
        let best = null;
        for (const product of index.productsByIngredient.get(ingredientId) ?? []) {
            const price = at(index.byProduct.get(product.id));
            if (!price)
                continue;
            if (!best || dec(price.observation.price).lessThan(best.price.observation.price))
                best = { price, product };
        }
        if (best)
            return { ...best, via: 'INGREDIENT_PRODUCT' };
        // 4. a price recorded for the ingredient itself (loose goods)
        const price = at(index.byIngredient.get(ingredientId));
        if (price)
            return { price, via: 'INGREDIENT', product: null };
    }
    // 5. none
    return null;
}
function estimateWith(items, index, options) {
    const open = items.filter((item) => !item.checked);
    const lines = [];
    const missing = [];
    let total = dec(0);
    for (const item of open) {
        const resolved = resolveItemPrice(item, index, options);
        if (!resolved) {
            missing.push(item.id);
            continue;
        }
        const multiplier = priceMultiplier(item, resolved.price.observation, resolved.product);
        const amount = dec(resolved.price.observation.price).times(multiplier);
        total = total.plus(amount.toDecimalPlaces(2, Dec.ROUND_HALF_UP));
        lines.push({
            itemId: item.id,
            amount: money(amount),
            multiplier: multiplier.toDecimalPlaces(3).toString(),
            via: resolved.via,
            productId: resolved.product?.id ?? resolved.price.observation.productId,
            observation: resolved.price.observation,
            stale: resolved.price.stale,
            viaChain: resolved.price.viaChain,
        });
    }
    return {
        storeId: options.store.id,
        total: money(total),
        currency: options.currency,
        pricedItems: lines.length,
        openItems: open.length,
        enough: open.length > 0 && lines.length * 2 >= open.length,
        approximate: lines.some((line) => line.stale),
        lines,
        missing,
    };
}
export function estimateList(items, observations, products, options) {
    return estimateWith(items, indexPrices(observations, products), options);
}
export const COMMON_BASKET_MIN_ITEMS = 3;
export function compareStores(items, observations, products, stores, options) {
    const index = indexPrices(observations, products);
    const estimates = stores.map((store) => estimateWith(items, index, { ...options, store }));
    // Stores with too few prices are listed without a total and stay out of the basket.
    const compared = estimates.filter((estimate) => estimate.enough);
    if (compared.length < 2)
        return { stores: estimates, commonBasket: null };
    const itemIds = compared
        .map((estimate) => new Set(estimate.lines.map((line) => line.itemId)))
        .reduce((common, next) => new Set([...common].filter((id) => next.has(id))));
    if (itemIds.size < COMMON_BASKET_MIN_ITEMS)
        return { stores: estimates, commonBasket: null };
    const totals = compared.map((estimate) => ({
        storeId: estimate.storeId,
        total: money(estimate.lines
            .filter((line) => itemIds.has(line.itemId))
            .reduce((sum, line) => sum.plus(line.amount), dec(0))),
    }));
    const sorted = [...totals].sort((a, b) => dec(a.total).comparedTo(b.total));
    // A tie names nobody.
    const cheapestStoreId = sorted.length > 1 && dec(sorted[0].total).equals(sorted[1].total)
        ? null
        : (sorted[0]?.storeId ?? null);
    return {
        stores: estimates,
        commonBasket: { itemIds: [...itemIds], totals, cheapestStoreId },
    };
}
//# sourceMappingURL=estimate-list.js.map